import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FaqItem {
  faqId: string;
  categoryId: string | null;
  question: string;
  answer: string;
  displayOrder: number;
}

interface FaqAccordionProps {
  faqs: FaqItem[];
  categoryNameMap?: Map<string, string>;
}

function AccordionItem({
  faq,
  isOpen,
  onToggle,
}: {
  faq: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-border last:border-b-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between py-4 text-left text-sm font-medium transition-colors hover:text-foreground/80"
      >
        <span className="pr-4">{faq.question}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-200',
          isOpen ? 'max-h-[1000px] opacity-100 pb-4' : 'max-h-0 opacity-0',
        )}
      >
        <div className="text-sm text-muted-foreground whitespace-pre-wrap">{faq.answer}</div>
      </div>
    </div>
  );
}

export function FaqAccordion({ faqs, categoryNameMap }: FaqAccordionProps) {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  if (faqs.length === 0) {
    return <div className="py-8 text-center text-sm text-muted-foreground">No FAQs available.</div>;
  }

  // Group FAQs by categoryId
  const grouped = faqs
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .reduce<Record<string, FaqItem[]>>((acc, faq) => {
      const key = faq.categoryId ?? 'uncategorized';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(faq);
      return acc;
    }, {});

  const categoryIds = Object.keys(grouped);
  const hasMultipleCategories = categoryIds.length > 1;

  const toggleItem = (faqId: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(faqId)) {
        next.delete(faqId);
      } else {
        next.add(faqId);
      }
      return next;
    });
  };

  const toggleCategory = (categoryId: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const getCategoryName = (categoryId: string): string => {
    if (categoryId === 'uncategorized') return 'Other';
    return categoryNameMap?.get(categoryId) ?? categoryId;
  };

  return (
    <div className="space-y-4">
      {categoryIds.map((categoryId) => {
        const isCollapsed = collapsedCategories.has(categoryId);
        const categoryFaqs = grouped[categoryId];
        const categoryName = getCategoryName(categoryId);

        return (
          <div key={categoryId} className="rounded-lg border border-border overflow-hidden">
            {/* Category header with caret toggle */}
            {hasMultipleCategories && (
              <button
                onClick={() => toggleCategory(categoryId)}
                className="flex w-full items-center gap-2 px-4 py-3 bg-muted/50 hover:bg-muted/80 transition-colors text-left"
              >
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                )}
                <span className="text-sm font-semibold">{categoryName}</span>
                <span className="text-xs text-muted-foreground ml-auto">
                  {categoryFaqs.length} {categoryFaqs.length === 1 ? 'question' : 'questions'}
                </span>
              </button>
            )}

            {/* FAQ items within category */}
            <div
              className={cn(
                'overflow-hidden transition-all duration-200',
                isCollapsed ? 'max-h-0' : 'max-h-[5000px]',
              )}
            >
              <div className="px-4">
                {categoryFaqs.map((faq) => (
                  <AccordionItem
                    key={faq.faqId}
                    faq={faq}
                    isOpen={openItems.has(faq.faqId)}
                    onToggle={() => toggleItem(faq.faqId)}
                  />
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
